import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ControlStatus } from '@bounded/api-client';
import { api } from '../../api/client';
import { q } from '../../api/queries';
import { useNames } from '../../api/names';
import { ConfirmationChip, Countdown, ErrorNotice, Provenance } from '@bounded/ui/state';
import { Button, Empty, Facts, Mono, Panel, SectionHeader, Select, SettingRow, Skeleton, Table, col } from '@bounded/ui/primitives';
import { useNow } from '@bounded/ui/time';
import { useFormat, useT } from '../../i18n';
import { ConfigEditor, isObject } from '@bounded/ui/config-editor';

export const Route = createFileRoute('/_app/projects/$projectId/devices/$deviceId')({ component: TargetPage });

function TargetPage() {
  const t = useT();
  const { projectId, deviceId } = Route.useParams();
  const names = useNames();
  return (
    <div className="grid gap-6">
      <SectionHeader title={names.device(deviceId)}
        description={<>{t.target.intro(names.project(projectId))} <Link to="/devices/$deviceId" params={{ deviceId }} className="text-boundary hover:underline">{t.target.deviceDetails}</Link></>} />
      <DeviceSettings deviceId={deviceId} projectId={projectId} />
      <Tasks deviceId={deviceId} projectId={projectId} />
      <Grants deviceId={deviceId} projectId={projectId} />
    </div>
  );
}

type AuditMode = ControlStatus['desired']['audit_mode'];
const AUDIT_MODES: AuditMode[] = ['off', 'summary', 'full'];
const WINDOWS = [60, 300, 900, 3_600, 4 * 3_600, 24 * 3_600];

/**
 * Lock, uploads and offline window in one panel: they are all "how this project runs on this device",
 * and one control column keeps them aligned. Confirmation state sits in the header, requested vs.
 * reported in the footer.
 */
function DeviceSettings({ deviceId, projectId }: { deviceId: string; projectId: string }) {
  const t = useT(); const m = t.target; const f = useFormat();
  const queryClient = useQueryClient();
  const controls = useQuery(q.controls(deviceId, projectId));
  const offline = useQuery(q.offline(deviceId, projectId));
  // Switching uploads to full needs a confirmation; `locked` is what the change asked for alongside it.
  const [confirmFull, setConfirmFull] = useState<false | { locked: boolean }>(false);
  const set = useMutation({
    mutationFn: (next: { locked: boolean; audit_mode: AuditMode }) => api.setControls(deviceId, projectId, { expected_revision: controls.data!.desired.revision, ...next }),
    onSuccess: (data) => { queryClient.setQueryData(q.controls(deviceId, projectId).queryKey, data); setConfirmFull(false); },
  });
  const setWindow = useMutation({
    mutationFn: (seconds: number) => api.setOffline(deviceId, projectId, offline.data!.revision, seconds),
    onSuccess: (data) => { queryClient.setQueryData(q.offline(deviceId, projectId).queryKey, data); void queryClient.invalidateQueries({ queryKey: ['target', deviceId, projectId, 'grants'] }); },
  });
  const c = controls.data;
  const confirmed = !!c && c.device_confirmed && c.observed?.revision === c.desired.revision;
  const current = offline.data?.offline_seconds;
  const windows = current !== undefined && !WINDOWS.includes(current) ? [...WINDOWS, current].sort((a, b) => a - b) : WINDOWS;

  return (
    <Panel title={m.settingsTitle}
      actions={c && (confirmed ? <ConfirmationChip state="confirmed">{m.confirmed}</ConfirmationChip> : <ConfirmationChip state="pending">{m.waiting}</ConfirmationChip>)}
      footer={c && (
        <div className="grid gap-3">
          <Facts items={[
            [m.askedFor, m.askedValue(c.desired.locked, m.audit[c.desired.audit_mode], c.desired.revision)],
            [m.reports, c.observed ? m.reportedValue(c.observed.revision, !!c.observed.local_locked) : m.nothingYet],
          ]} />
          <Provenance>{confirmed ? m.offlineNote : `${m.unconfirmed(c.device_recent)} ${m.offlineNote}`}</Provenance>
        </div>
      )}>
      {(controls.error || set.error || setWindow.error) && (
        <div className="grid gap-2 border-b border-rule p-4">
          {controls.error && <ErrorNotice error={controls.error} onRetry={() => void controls.refetch()} />}
          {set.error && <ErrorNotice error={set.error} onRefresh={() => { set.reset(); void controls.refetch(); }} />}
          {setWindow.error && <ErrorNotice error={setWindow.error} onRefresh={() => { setWindow.reset(); void offline.refetch(); }} />}
        </div>
      )}
      {!c ? <div className="p-4"><Skeleton className="h-32" /></div> : <>
        {/* The same three settings as JSON. Applying sends only what changed; each still takes effect on the device as soon as it syncs. */}
        <ConfigEditor id="device-settings-json" inset commit="apply" rows={6} disabled={set.isPending || setWindow.isPending || !offline.data}
          value={{ locked: c.desired.locked, audit_mode: c.desired.audit_mode, offline_seconds: current ?? null }}
          validate={(v) => (isObject(v) && typeof v.locked === 'boolean' && AUDIT_MODES.includes(v.audit_mode as AuditMode) && Number.isInteger(v.offline_seconds) ? null : m.settingsShape)}
          onChange={(next) => {
            const want = next as { locked: boolean; audit_mode: AuditMode; offline_seconds: number };
            if (want.offline_seconds !== current) setWindow.mutate(want.offline_seconds);
            if (want.audit_mode === 'full' && c.desired.audit_mode !== 'full') setConfirmFull({ locked: want.locked });
            else if (want.locked !== c.desired.locked || want.audit_mode !== c.desired.audit_mode) set.mutate({ locked: want.locked, audit_mode: want.audit_mode });
          }}>
        <div>
        <SettingRow label={m.lockTitle} description={m.lockBody}
          control={<Button className="w-full" variant={c.desired.locked ? 'secondary' : 'danger'} disabled={set.isPending} onClick={() => set.mutate({ locked: !c.desired.locked, audit_mode: c.desired.audit_mode })}>{c.desired.locked ? m.unlock : m.lock}</Button>} />
        <SettingRow label={m.uploadsTitle} htmlFor="audit-mode" description={m.uploadsBody}
          control={
            <Select id="audit-mode" value={c.desired.audit_mode} disabled={set.isPending}
              onChange={(e) => { const mode = e.target.value as AuditMode; if (mode === 'full') setConfirmFull({ locked: c.desired.locked }); else set.mutate({ locked: c.desired.locked, audit_mode: mode }); }}>
              {AUDIT_MODES.map((k) => <option key={k} value={k}>{m.audit[k]}</option>)}
            </Select>
          } />
        {confirmFull && (
          <div className="grid gap-2 border-b border-rule bg-ask/5 px-4 py-4">
            <p className="font-medium">{m.fullTitle}</p>
            <p className="max-w-[72ch] text-sm text-graphite">{m.fullBody}</p>
            <div className="flex gap-2"><Button size="sm" variant="primary" onClick={() => set.mutate({ locked: confirmFull.locked, audit_mode: 'full' })}>{m.fullConfirm}</Button><Button size="sm" variant="ghost" onClick={() => setConfirmFull(false)}>{t.common.cancel}</Button></div>
          </div>
        )}
        <SettingRow label={m.keepFor} htmlFor="offline-window" description={m.offlineBody}
          control={offline.data
            ? <Select id="offline-window" value={current} disabled={setWindow.isPending} onChange={(e) => setWindow.mutate(Number(e.target.value))}>
                {windows.map((s) => <option key={s} value={s}>{m.windows[s] ?? f.duration(s, false)}</option>)}
              </Select>
            : <Skeleton className="h-8" />} />
        </div>
        </ConfigEditor>
      </>}
    </Panel>
  );
}

/** Tasks this device has asked about, from the control plane's task listing. Ending one is a permanent tombstone for that id. */
function Tasks({ deviceId, projectId }: { deviceId: string; projectId: string }) {
  const t = useT(); const m = t.target; const f = useFormat();
  const now = useNow(10_000);
  const queryClient = useQueryClient();
  const tasks = useQuery(q.tasks(projectId));
  const [closing, setClosing] = useState<string | null>(null);
  const close = useMutation({
    mutationFn: (task: string) => api.closeTask(deviceId, projectId, task),
    onSuccess: () => setClosing(null),
    onSettled: async () => { await queryClient.invalidateQueries({ queryKey: ['project', projectId, 'tasks'] }); await queryClient.invalidateQueries({ queryKey: ['target', deviceId, projectId, 'grants'] }); },
  });
  const items = (tasks.data?.items ?? []).filter((x) => x.device_id === deviceId);
  return (
    <Panel title={m.tasksTitle} description={m.tasksDescription}>
      {tasks.isPending && <div className="p-4"><Skeleton className="h-16" /></div>}
      {tasks.error && <div className="p-4"><ErrorNotice error={tasks.error} onRetry={() => void tasks.refetch()} /></div>}
      {close.error && <div className="p-4"><ErrorNotice error={close.error} /></div>}
      {tasks.data && items.length === 0 && <Empty title={m.noTasks}>{m.noTasksBody}</Empty>}
      {closing && (
        <div className="grid gap-2 border-b border-rule bg-deny/5 px-4 py-4">
          <p className="font-medium">{m.endTitle(<code>{closing}</code>)}</p>
          <p className="max-w-[72ch] text-sm text-graphite">{m.endBody}</p>
          <div className="flex gap-2"><Button size="sm" variant="danger" disabled={close.isPending} onClick={() => close.mutate(closing)}>{m.end}</Button><Button size="sm" variant="ghost" onClick={() => setClosing(null)}>{t.common.cancel}</Button></div>
        </div>
      )}
      {items.length > 0 && (
        <Table minWidth="42rem" head={[{ label: m.taskCols.task }, { label: m.taskCols.lastSeen, className: 'w-36' }, { label: m.taskCols.waiting, className: `w-24 ${col.num}` }, { label: m.taskCols.grants, className: `w-28 ${col.num}` }, { className: col.action }]}>
          {items.map((x) => (
            <tr key={x.task_id}>
              <td className="whitespace-nowrap"><code>{x.task_id}</code></td>
              <td className="text-graphite">{x.last_seen_at ? f.ago(x.last_seen_at, now) : '—'}</td>
              <td className={col.num}>{x.waiting_approvals}</td>
              <td className={col.num}>{x.active_grants}</td>
              <td className={col.action}>{x.closed_at ? <span className="text-sm text-graphite">{m.ended(f.ago(x.closed_at, now))}</span>
                : <Button size="sm" variant="ghost" className="-mr-2.5" disabled={close.isPending} onClick={() => setClosing(x.task_id)}>{m.end}</Button>}</td>
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  );
}

function Grants({ deviceId, projectId }: { deviceId: string; projectId: string }) {
  const t = useT(); const m = t.target; const f = useFormat();
  const queryClient = useQueryClient();
  const grants = useQuery(q.grants(deviceId, projectId));
  const revoke = useMutation({
    mutationFn: (g: { grant_id: string; revision: number }) => api.revokeGrant(deviceId, projectId, g.grant_id, g.revision),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['target', deviceId, projectId, 'grants'] }),
  });
  const data = grants.data;
  return (
    <Panel title={m.grantsTitle} description={m.grantsDescription}
      actions={data && (data.sync.device_confirmed ? <ConfirmationChip state="confirmed">{m.upToDate}</ConfirmationChip> : <ConfirmationChip state="pending">{m.notSynced}</ConfirmationChip>)}
      footer={data && <Provenance>{m.grantsNote(f.duration(data.revocation_max_staleness_seconds, false))}</Provenance>}>
      {revoke.error && <div className="p-4"><ErrorNotice error={revoke.error} onRefresh={() => void grants.refetch()} /></div>}
      {data?.items.length === 0 && <Empty title={m.noGrants}>{m.noGrantsBody}</Empty>}
      {!!data?.items.length && (
        <Table minWidth="52rem" head={[{ label: m.cols.allows }, { label: m.cols.task, className: 'w-52' }, { label: m.cols.used, className: `w-24 ${col.num}` }, { label: m.cols.ends, className: 'w-32' }, { className: col.action }]}>
          {data.items.map(({ grant: g, reported_uses }) => (
            <tr key={g.grant_id}>
              <td><code>{g.operations.join(', ')}</code><div className="mt-0.5 text-sm text-graphite">{g.resources.map((r) => <Mono key={r.resource.locator} value={r.resource.locator} head={28} tail={16} />)}</div></td>
              <td className="whitespace-nowrap"><code>{g.scope.task_id}</code></td>
              <td className={col.num}>{m.usedOf(reported_uses, g.max_uses)}</td>
              <td>{g.status === 'active' ? <Countdown until={g.expires_at} /> : <span className="text-graphite">{m.grantStatus[g.status] ?? g.status}</span>}</td>
              <td className={col.action}>{g.status === 'active' && <Button size="sm" variant="danger" disabled={revoke.isPending} onClick={() => revoke.mutate(g)}>{m.revoke}</Button>}</td>
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  );
}
